/**
 * CRM Conversations (Active / No reply yet / Archived)
 *
 * Conversation-centric lists over the lead inbox data, driven by the
 * denormalized conversation state on crm.contacts (last_inbound_at /
 * last_outbound_at / last_activity_at / archived_at):
 *   - active      : the lead has replied at least once (and isn't archived)
 *   - awaiting    : we've reached out, no response yet (and isn't archived)
 *   - needs_reply : the lead replied and we still owe a response (subset of
 *                   active, server-derived from needs_reply_at)
 *   - archived    : recruiter archived it (an inbound reply auto-reopens)
 * Rows are chat-style cards (avatar, name + study + recency, last-message
 * snippet with a direction arrow) with inline owner assignment and an
 * archive/unarchive action (optimistic removal, re-inserted on failure).
 * Mounted at /crm/active and /crm/awaiting; the active view has an
 * "Awaiting my reply" toggle (default on, narrows to needs_reply) and a
 * "Show archived" toggle that flips the list to the archived set. Assignment
 * is filterable in both modes: a multi-select of recruiters (plus "include
 * unassigned") under Add filter, with "Mine" as its sole-me shortcut.
 *
 * PHONES (useIsPhone, below `sm`): the search box takes a full-width row of
 * its own above the swipeable chip strip, so the view toggles (Awaiting my
 * reply, Agent-owned, Show archived, …) are on screen instead of pushed past
 * its edge by a 224px input; a card's last-message snippet shows two lines,
 * chat-app style, instead of one truncated line. Desktop renders as before.
 */
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Archive,
  ArchiveRestore,
  ArrowDownLeft,
  ArrowUpRight,
  Bot,
  Hourglass,
  Loader2,
  MessagesSquare,
  MoonStar,
  StickyNote,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { useSharedNavCounts } from '../useNavCounts';
import { ContactListItem, ConversationFilter, StudyOption, contactName, studyInternalLabel } from '../types';
import { INCOMING_ELIGIBILITY_OPTIONS, MILESTONE_FILTER_OPTIONS, MILESTONE_LABELS, getPref, initials, needsReply, notifyStatsChanged, prettySlug, relTime, setPref, useIsPhone, useTeam } from '../lib';
import { ChannelIcon, StudyChips, RowLink, useRowNav } from '../components/shared';
import { ActiveFilters, AddFilterMenu, FilterBar, FilterChip, FilterMultiSelect, FilterSelect, MilestoneChip, OwnerSelect, PageHeader, RefreshButton, SearchInput, SnoozeButton, isActiveSnooze } from '../components';
import type { ActiveChip, FilterDef } from '../components';
import { Alert } from '../components/ui';
import { useWindowedSlice } from '../useWindowedSlice';

const LIMIT = 50;

const errMsg = (e: any): string =>
  e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'Request failed';

/** Snippet fallback when the last message has no body (calls, status-only rows). */
const channelWord = (channel: string): string => {
  if (channel === 'sms') return 'SMS';
  if (channel === 'call') return 'Call';
  if (channel === 'email') return 'Email';
  return prettySlug(channel);
};

/** Conversation-recency sort key (matches the API's sort=activity ordering). */
const activityTs = (c: ContactListItem): number | null => {
  const iso = c.last_activity_at || c.last_comm?.occurred_at;
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? null : t;
};

const COPY: Record<ConversationFilter, { title: string; subtitle: string; emptyTitle: string; emptyHint: string }> = {
  active: {
    title: 'All active conversations',
    subtitle: 'Leads who have replied — keep these moving.',
    emptyTitle: 'No active conversations',
    emptyHint: 'When a lead replies, the conversation lands here.',
  },
  awaiting: {
    title: 'No reply yet',
    subtitle: 'You have reached out; no response yet.',
    emptyTitle: 'Nothing awaiting a reply',
    emptyHint: 'Leads you have contacted but who have not responded yet will appear here.',
  },
  needs_reply: {
    title: 'Awaiting your reply',
    subtitle: "Patients who messaged and haven't heard back yet.",
    emptyTitle: 'All caught up',
    emptyHint: 'No leads are waiting on a reply right now.',
  },
  archived: {
    title: 'Archived',
    subtitle: 'Archived conversations — an inbound reply reopens them automatically.',
    emptyTitle: 'No archived conversations',
    emptyHint: 'Archive a conversation to move it out of the active list.',
  },
  agent: {
    title: 'Agent-owned',
    subtitle: 'Leads the texting agent is working — they leave the recruiter worklists; supervise them in the Agent console.',
    emptyTitle: 'No agent-owned conversations',
    emptyHint: 'When the AI texting agent owns a thread, the lead appears here instead of the worklists.',
  },
};

interface Filters {
  q: string;
  /** Assigned recruiter(s) — owner_user_id list (empty = anyone). Combines with
   *  `unassigned` as an OR server-side, the same contract the leads inbox uses.
   *  "Mine" is the sole-current-user case of this one filter, not a separate
   *  parameter — two things writing `owner` would fight each other. */
  owner: string[];
  /** Also include leads nobody is assigned to (owner_user_id IS NULL). */
  unassigned: boolean;
  /** study_slug(s) the lead is being considered for (empty = all studies). */
  study: string[];
  /** Incoming (landing-form) eligibility verdict(s) (empty = any). */
  eligibility: string[];
  /** Derived pertinent-milestone key(s) (empty = any). */
  milestone: string[];
  /** Sort order: 'activity' (default, recency), 'eligibility' (incoming verdict),
   *  '' (newest), 'oldest'. */
  sort: string;
  /** Active view only: narrow to leads that owe a recruiter reply (needs_reply).
   *  Defaults on (persisted) so the active view foregrounds waiting patients. */
  needsReplyOnly: boolean;
  /** Active view only: flip the list to the archived set. */
  showArchived: boolean;
  /** Active view only (M-15): flip to the AGENT-OWNED set — the compensating
   *  visibility for the worklist exclusion (agent-owned leads leave every
   *  other view; this chip is where a recruiter finds them). */
  agentOnly: boolean;
  /** Working views only: reveal leads snoozed to a future time (hidden by default). */
  showSnoozed: boolean;
}

/** localStorage pref key (namespaced under `crm.` by getPref/setPref). */
const NEEDS_REPLY_PREF = 'conversations.needsReplyOnly';

const DEFAULT_FILTERS: Filters = {
  q: '',
  owner: [],
  unassigned: false,
  study: [],
  eligibility: [],
  milestone: [],
  sort: 'activity', // conversations are recency-triaged by default
  // Seed from the saved pref so a recruiter who turns it off stays off.
  needsReplyOnly: getPref(NEEDS_REPLY_PREF, 'true') === 'true',
  showArchived: false,
  agentOnly: false,
  showSnoozed: false,
};

/** Per-mode pref key so Active and No-reply keep their own filter sets. */
const FILTERS_PREF = 'conversations.filters';

/** Coerce a persisted multi-select value to string[] (old scalar -> [scalar]). */
const toArr = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string' && x !== '')
  : typeof v === 'string' && v !== '' ? [v]
  : [];

/**
 * Load this mode's persisted filters, merged over the defaults. `userId` feeds
 * only the legacy migration below — a saved `mine: true` predates the owner
 * multi-select, so it has to be rewritten as "owner = me" to survive; without a
 * signed-in id that one flag is simply dropped.
 */
function loadFilters(mode: 'active' | 'awaiting', userId: string): Filters {
  const raw = getPref(`${FILTERS_PREF}.${mode}`, '');
  if (!raw) return DEFAULT_FILTERS;
  try {
    // `mine` is destructured OUT of the rest, so the retired flag stops being
    // re-persisted the first time this mode's filters are touched.
    const { mine, ...parsed } = JSON.parse(raw) as Partial<Filters> & { mine?: boolean };
    const merged: Filters = { ...DEFAULT_FILTERS, ...parsed };
    // Multi-select migration: an old persisted scalar becomes a one-element array.
    merged.study = toArr(parsed.study);
    merged.eligibility = toArr(parsed.eligibility);
    merged.milestone = toArr(parsed.milestone);
    merged.owner = toArr(parsed.owner);
    merged.unassigned = parsed.unassigned === true;
    if (mine === true && userId && merged.owner.length === 0) merged.owner = [userId];
    return merged;
  } catch {
    return DEFAULT_FILTERS;
  }
}

/**
 * One conversation card.
 *
 * Extracted and memoized (D-9): at ~150 open conversations, every keystroke in
 * the search box, every owner assignment and every archive re-rendered all of
 * them. Memo only pays if the props are shallow-stable, so this takes
 * PRIMITIVES and stable callbacks — never the parent's filter object, never a
 * freshly-built handler — and the parent's row handlers are useCallback'd with
 * refs behind them. The markup is byte-for-byte the inline card it replaces.
 */
const ConversationRow = memo(function ConversationRow({
  c, isArchivedView, rowNav, onOpen, onOwnerChanged, onToggleArchived, onSnooze,
}: {
  c: ContactListItem;
  isArchivedView: boolean;
  rowNav: (to: string) => { onClick: (e: React.MouseEvent) => void; onAuxClick: (e: React.MouseEvent) => void };
  onOpen: (id: string) => void;
  onOwnerChanged: (id: string, owner: string | null) => void;
  onToggleArchived: (row: ContactListItem, archive: boolean) => void;
  onSnooze: (row: ContactListItem, untilIso: string | null) => void;
}) {
  const reply = needsReply(c.last_comm);
  const lc = c.last_comm;
  return (
    <div
      role="button"
      tabIndex={0}
      {...rowNav(`/crm/contacts/${c.id}`)}
      onKeyDown={(e) => {
        // Only when the row itself is focused — Enter/Space on an
        // embedded select/button must not navigate away.
        if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) {
          if (e.key === ' ') e.preventDefault(); // don't scroll the page
          onOpen(c.id);
        }
      }}
      className="flex cursor-pointer items-start gap-3 rounded-xl border border-gray-200 bg-white p-3 shadow-sm transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700/50 sm:p-4"
    >
      <span
        aria-hidden="true"
        className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-primary-100 text-xs font-medium text-primary-700 dark:bg-primary-900/40 dark:text-primary-300"
      >
        {initials(contactName(c))}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <RowLink
            to={`/crm/contacts/${c.id}`}
            title={contactName(c)}
            className={`min-w-0 truncate text-sm text-gray-900 dark:text-gray-100 ${reply ? 'font-semibold' : 'font-medium'}`}
          >
            {contactName(c)}
          </RowLink>
          {reply && <span className="h-2 w-2 flex-shrink-0 rounded-full bg-primary-500" title="Needs your reply" />}
          {!isArchivedView && isActiveSnooze(c.snoozed_until) && (
            <span className="flex-shrink-0 rounded bg-primary-100 px-1.5 py-0.5 text-[10px] font-medium text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">snoozed</span>
          )}
          {/* Phones move the study chip down beside the milestone so
              the name row stays name + time, chat-app style. */}
          {(c.studies || []).length > 0 && (
            <span className="hidden min-w-0 sm:inline"><StudyChips studies={c.studies} max={1} /></span>
          )}
          <span className="ml-auto flex-shrink-0 text-xs text-gray-400 dark:text-gray-500">
            {relTime(c.last_activity_at || lc?.occurred_at)}
          </span>
        </div>
        {/* Phones show two lines of the snippet (chat-app style); desktop
            keeps the one truncated line. The icon tops out at the first line. */}
        <div className="mt-0.5 flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 max-sm:items-start">
          {lc ? (
            lc.channel === 'note' ? (
              // Internal note — never sent to the lead, so no direction arrow.
              <>
                <StickyNote aria-hidden="true" className="h-4 w-4 flex-shrink-0 text-amber-500 max-sm:mt-0.5" />
                <span className="min-w-0 truncate max-sm:line-clamp-2 max-sm:whitespace-normal">{lc.body ? `Note: ${lc.body}` : channelWord(lc.channel)}</span>
              </>
            ) : (
              <>
                {lc.direction === 'inbound' ? (
                  <ArrowDownLeft aria-hidden="true" className="h-4 w-4 flex-shrink-0 text-primary-600 dark:text-primary-400 max-sm:mt-0.5" />
                ) : (
                  <ArrowUpRight aria-hidden="true" className="h-4 w-4 flex-shrink-0 text-gray-400 max-sm:mt-0.5" />
                )}
                {!lc.body && <ChannelIcon channel={lc.channel} />}
                <span className="min-w-0 truncate max-sm:line-clamp-2 max-sm:whitespace-normal">{lc.body || channelWord(lc.channel)}</span>
              </>
            )
          ) : (
            <span className="min-w-0 truncate text-gray-400 dark:text-gray-500">No messages yet</span>
          )}
        </div>
        {(c.milestone || (c.studies || []).length > 0) && (
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            {(c.studies || []).length > 0 && (
              <span className="min-w-0 sm:hidden"><StudyChips studies={c.studies} max={1} /></span>
            )}
            {c.milestone && <MilestoneChip milestone={c.milestone} />}
          </div>
        )}
      </div>
      {/* Phones keep ONE action (archive) so rows read like a chat
          list; assignment + snooze live on the lead itself there.
          Desktop keeps the full inline triage cluster. */}
      <div className="flex flex-shrink-0 items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
        {/* Bound the select's width so the name/snippet keep room at 375px. */}
        <span className="hidden min-w-0 sm:inline-flex sm:max-w-[11rem]">
          <OwnerSelect
            contactId={c.id}
            value={c.owner_user_id}
            compact
            onChanged={(o) => onOwnerChanged(c.id, o)}
          />
        </span>
        {!isArchivedView && (
          <span className="hidden sm:inline-flex">
            <SnoozeButton
              snoozedUntil={c.snoozed_until}
              onSnooze={(iso) => onSnooze(c, iso)}
              label={contactName(c)}
            />
          </span>
        )}
        <button
          onClick={() => onToggleArchived(c, !isArchivedView)}
          aria-label={isArchivedView ? 'Unarchive conversation' : 'Archive conversation'}
          title={isArchivedView ? 'Unarchive' : 'Archive conversation'}
          className="inline-flex h-11 w-11 items-center justify-center rounded-lg border border-gray-300 text-gray-500 transition-colors duration-150 hover:bg-gray-50 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200 sm:h-8 sm:w-8"
        >
          {isArchivedView ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
});

export default function Conversations({ mode }: { mode: 'active' | 'awaiting' }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const rowNav = useRowNav();
  // Header count + (active view) the reconciliation subtitle both read the
  // shell's shared nav counts — the same source behind the sidebar badge and
  // the mobile tab bar, so this page can never show a different number for
  // the same thing (see useNavCounts.ts).
  const sharedConversations = useSharedNavCounts().conversations;
  // Recruiter roster for the assignment filter (session-cached, shared with
  // every OwnerSelect on the page — no extra request).
  const { team } = useTeam();
  // The ONE breakpoint read: where the search box mounts (header comment).
  const isPhone = useIsPhone();

  const [rows, setRows] = useState<ContactListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [filters, setFilters] = useState<Filters>(() => loadFilters(mode, user?.id || ''));
  const [studies, setStudies] = useState<StudyOption[]>([]);
  const filtersRef = useRef<Filters>(filters);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Refs mirror props/auth so the stable runQuery callback never reads stale
  // values (assigned during render, so they're current before any effect runs).
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const userIdRef = useRef(user?.id || '');
  userIdRef.current = user?.id || '';
  // Mirrored for toggleRowArchived: after an optimistic removal empties the
  // visible page, it must see the post-removal length and current hasMore.
  const rowCountRef = useRef(0);
  rowCountRef.current = rows.length;
  const hasMoreRef = useRef(false);
  hasMoreRef.current = hasMore;

  // Monotonic request id: a response only commits if it is still the newest
  // query, so a slow earlier fetch can never clobber a newer filter's results
  // and an in-flight Load-more can't append rows from a previous filter.
  const reqSeqRef = useRef(0);

  const viewOf = (m: 'active' | 'awaiting', f: Filters): ConversationFilter =>
    m === 'active' && f.showArchived ? 'archived'
      : m === 'active' && f.agentOnly ? 'agent'
        : m === 'active' && f.needsReplyOnly ? 'needs_reply'
          : m;

  const runQuery = useCallback(async (offset: number, replace: boolean) => {
    const seq = ++reqSeqRef.current;
    if (replace) {
      setLoading(true);
      setError(null);
    } else {
      setLoadingMore(true);
    }
    const f = filtersRef.current;
    const params: Record<string, string | number> = {
      conversation: viewOf(modeRef.current, f),
      limit: LIMIT,
      offset,
    };
    // 'activity' (the default) and 'eligibility' are sent; '' falls through to
    // the backend's newest-first default.
    if (f.sort) params.sort = f.sort;
    if (f.q.trim()) params.q = f.q.trim();
    // Assignment: owner IN (...) OR owner IS NULL — the backend ORs the two,
    // so both may be sent together.
    if (f.owner.length) params.owner = f.owner.join(',');
    if (f.unassigned) params.unassigned = 'true';
    if (f.study.length) params.study = f.study.join(',');
    if (f.eligibility.length) params.eligibility = f.eligibility.join(',');
    if (f.milestone.length) params.milestone = f.milestone.join(',');
    // Hide future-snoozed leads unless "Show snoozed" is on — including the
    // needs_reply view (a new inbound wakes the snooze DB-side, so a hidden
    // lead is one who has stayed quiet). The backend ignores this only for the
    // archived view, which is not a working list.
    params.show_snoozed = f.showSnoozed ? 'true' : 'false';
    try {
      const res = await api.get('/api/crm/contacts', { params });
      if (seq !== reqSeqRef.current) return; // stale response — a newer query owns the list
      const batch: ContactListItem[] = res.data?.contacts || [];
      setRows((prev) => {
        if (replace) return batch;
        // Dedup on append: a conversation touched between pages shifts the
        // offset and would repeat the page-boundary rows (duplicate keys).
        const seen = new Set(prev.map((r) => r.id));
        return [...prev, ...batch.filter((b) => !seen.has(b.id))];
      });
      setHasMore(batch.length === LIMIT);
    } catch (e: any) {
      if (seq === reqSeqRef.current) setError(errMsg(e));
    } finally {
      if (seq === reqSeqRef.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, []);

  // Studies for the filter dropdown — fetched once. Tolerate failure silently:
  // the dropdown just stays empty (showing only "All studies") and the rest of
  // the page keeps working.
  useEffect(() => {
    let active = true;
    api.get('/api/crm/studies')
      .then((res) => { if (active) setStudies(res.data?.studies || []); })
      .catch(() => { /* no-op */ });
    return () => { active = false; };
  }, []);

  // Route (mode) change: load the target mode's persisted filters, reset the
  // list, re-query from page 0. (Also runs on mount, seeding the first fetch.)
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const f = loadFilters(mode, userIdRef.current);
    filtersRef.current = f;
    setFilters(f);
    setRows([]);
    setHasMore(false);
    runQuery(0, true);
  }, [mode, runQuery]);

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  /** Apply a filter change and re-query page 0 (text inputs debounce 300ms). */
  const apply = (patch: Partial<Filters>, debounce = false) => {
    const next = { ...filtersRef.current, ...patch };
    filtersRef.current = next;
    setFilters(next);
    setPref(`${FILTERS_PREF}.${mode}`, JSON.stringify(next)); // survive navigation
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (debounce) {
      debounceRef.current = setTimeout(() => runQuery(0, true), 300);
    } else {
      runQuery(0, true);
    }
  };

  const loadMore = () => runQuery(rows.length, false);
  const refresh = () => runQuery(0, true);

  const view = viewOf(mode, filters);
  const copy = COPY[view];
  // Working-view counts always exclude snoozed server-side; while "Show
  // snoozed" reveals them in the list, hide the header count to avoid showing
  // fewer than the visible rows. (The archived count is unaffected.) 'agent'
  // has no shared count (see ConversationCounts) — it renders no header count,
  // same as when the agent view served an undefined field here before.
  const count = sharedConversations && view !== 'agent' && !(filters.showSnoozed && view !== 'archived')
    ? sharedConversations[view]
    : null;
  // Active view only: the owner's "25 vs 528" confusion was two counts
  // answering different questions with no explanation on the page. Spell out
  // the second number right here, from the same shared source the sidebar
  // badge reads, so the two can never quietly disagree.
  const subtitle = view === 'active' && typeof sharedConversations?.needs_reply === 'number'
    ? `Leads who have replied — ${sharedConversations.needs_reply} still owe you an answer.`
    : copy.subtitle;
  const HeaderIcon = view === 'archived' ? Archive : view === 'agent' ? Bot : mode === 'active' ? MessagesSquare : Hourglass;
  const hasFilters = !!(filters.q.trim() || filters.owner.length || filters.unassigned || filters.study.length || filters.eligibility.length || filters.milestone.length);

  /**
   * Archive/unarchive a row: optimistic removal from the current list; on
   * failure the row is re-inserted in conversation-recency order and the
   * error banner shows. The header/badge counts are not bumped locally — they
   * read the shared nav context (useNavCounts), which refetches on the
   * notifyStatsChanged() ping below (same mechanism the sidebar badges use).
   */
  const toggleRowArchived = useCallback(async (row: ContactListItem, archive: boolean) => {
    setError(null);
    setRows((prev) => prev.filter((r) => r.id !== row.id));
    try {
      await api.patch(`/api/crm/contacts/${row.id}`, { archived: archive });
      notifyStatsChanged();
      // Don't strand the recruiter: if the optimistic removal emptied the
      // visible page while more rows exist, pull the next page in.
      if (rowCountRef.current === 0 && hasMoreRef.current) runQuery(0, true);
    } catch (e: any) {
      setRows((prev) => {
        if (prev.some((r) => r.id === row.id)) return prev;
        const next = [...prev, row];
        next.sort((a, b) => {
          const ta = activityTs(a);
          const tb = activityTs(b);
          if (ta === tb) return 0;
          if (ta === null) return 1; // nulls last
          if (tb === null) return -1;
          return tb - ta;
        });
        return next;
      });
      setError(errMsg(e));
    }
  }, [runQuery]);

  const onOwnerChanged = useCallback((id: string, owner: string | null) =>
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, owner_user_id: owner } : r))), []);

  /**
   * Per-row snooze: defer a lead to a future time (or un-snooze with null). In
   * every working view (active/awaiting/needs_reply — all of which hide snoozed
   * by default) a future snooze removes the row (the shared header/badge count
   * catches up on the next poll or STATS_CHANGED_EVENT, not instantly); with
   * "Show snoozed" on, or on un-snooze, snoozed_until updates in place. Only
   * `archived` never hides (enforced server-side) — it isn't a working list,
   * and it doesn't render the snooze action.
   */
  const snoozeRow = useCallback(async (row: ContactListItem, untilIso: string | null) => {
    // Read through the refs the page already maintains: a per-row handler that
    // closes over `view`/`filters` gets a new identity on every filter change
    // and would defeat the row memo.
    const f = filtersRef.current;
    const inFilteredView = viewOf(modeRef.current, f) !== 'archived';
    const nowSnoozed = isActiveSnooze(untilIso);
    const hiding = nowSnoozed && !f.showSnoozed && inFilteredView;
    setError(null);
    if (hiding) {
      setRows((prev) => prev.filter((r) => r.id !== row.id));
    } else {
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, snoozed_until: untilIso } : r)));
    }
    try {
      await api.patch(`/api/crm/contacts/${row.id}`, { snoozed_until: untilIso });
      // Working-view badge counts always exclude snoozed server-side — the
      // shared nav context (not local state) reconciles on this ping.
      notifyStatsChanged();
      if (hiding && rowCountRef.current === 0 && hasMoreRef.current) runQuery(0, true);
    } catch (e: any) {
      runQuery(0, true); // simplest reliable revert
      setError(errMsg(e));
    }
  }, [runQuery]);

  // Stable row handlers/refs for the memoized cards (D-9).
  const openContact = useCallback((id: string) => navigate(`/crm/contacts/${id}`), [navigate]);
  // Card windowing: the page is the scroller and the cards sit in a space-y-2
  // stack (8px gap), so the slice math works in row PITCH. Inert below 100 rows.
  const slice = useWindowedSlice({
    count: rows.length,
    gap: 8,
    estimatedRowHeight: 96,
  });

  // ---- Unified filter system (shared <FilterBar>) ----
  const studyLabel = (slug: string) => {
    const s = studies.find((x) => x.slug === slug);
    return s ? studyInternalLabel(s) || prettySlug(s.slug) : prettySlug(slug);
  };

  const ownerLabel = (id: string) => team.find((m) => m.id === id)?.name || 'Recruiter';

  // "Mine" is the exclusive shortcut for "assigned to me" — active only when
  // I'm the sole selection, so a broader set reads as the Assigned-to filter
  // (and turning Mine on clears any "include unassigned", which would widen
  // the list right back out).
  const mineActive = !!user?.id && filters.owner.length === 1 && filters.owner[0] === user.id;
  const toggleMine = () =>
    mineActive ? apply({ owner: [] }) : apply({ owner: user?.id ? [user.id] : [], unassigned: false });

  // Reset only the filtering dimensions (search + secondary filters + assignment),
  // preserving sort and the view-defining toggles (which conversation view
  // you're in: needs_reply / archived / show-snoozed).
  const clearFilters = () => apply({ q: '', owner: [], unassigned: false, study: [], eligibility: [], milestone: [] });

  // Secondary filters, revealed by the "Add filter" popover.
  const moreFilters: FilterDef[] = [
    ...(studies.length > 0
      ? [{
        type: 'multiselect',
        key: 'study',
        label: 'Study',
        values: filters.study,
        onChange: (v: string[]) => apply({ study: v }),
        options: [
          { value: '', label: 'All studies' },
          ...studies.map((s) => ({
            value: s.slug,
            label: (studyInternalLabel(s) || prettySlug(s.slug)) + (typeof s.count === 'number' ? ` (${s.count})` : ''),
          })),
        ],
      } as FilterDef]
      : []),
    {
      // Assignment is a multi-select PLUS an "unassigned" checkbox — the two
      // combine (owner IN (...) OR owner IS NULL), so one custom node holds
      // both. The visible "Mine" chip is the sole-me case of this same filter.
      type: 'custom',
      key: 'owner',
      label: 'Assigned to',
      node: (
        <div className="space-y-1.5">
          <FilterMultiSelect
            values={filters.owner}
            onChange={(v) => apply({ owner: v })}
            options={[{ value: '', label: 'Anyone' }, ...team.map((m) => ({ value: m.id, label: m.name }))]}
            label="Assigned to"
          />
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={filters.unassigned}
              onChange={(e) => apply({ unassigned: e.target.checked })}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            Include unassigned
          </label>
        </div>
      ),
    },
    {
      type: 'multiselect',
      key: 'eligibility',
      label: 'Incoming eligibility',
      values: filters.eligibility,
      onChange: (v) => apply({ eligibility: v }),
      title: "Best landing-form verdict across the lead's studies; with a study filter on, the verdict of that study's own form. Manually-added contacts (no form submission) aren't matched.",
      options: [{ value: '', label: 'Any eligibility' }, ...INCOMING_ELIGIBILITY_OPTIONS],
    },
    {
      type: 'multiselect',
      key: 'milestone',
      label: 'Milestone',
      values: filters.milestone,
      onChange: (v) => apply({ milestone: v }),
      title: 'The pertinent-milestone sub-line shown under each lead.',
      options: [
        { value: '', label: 'Any milestone' },
        ...MILESTONE_FILTER_OPTIONS.map((k) => ({ value: k as string, label: MILESTONE_LABELS[k] })),
      ],
    },
  ];

  // Count only the popover filters that actually constrain results (one per
  // selected value, matching the per-value chips below).
  const moreFiltersCount = filters.study.length + filters.owner.length + (filters.unassigned ? 1 : 0)
    + filters.eligibility.length + filters.milestone.length;

  // Applied-filter summary chips (one per selected value). The visible Mine
  // quick chip already covers itself, so only the secondary filters surface here.
  const activeChips: ActiveChip[] = [];
  filters.study.forEach((slug) =>
    activeChips.push({ key: `study-${slug}`, label: `Study: ${studyLabel(slug)}`, onRemove: () => apply({ study: filters.study.filter((s) => s !== slug) }) }),
  );
  // Assignment: one chip per recruiter, except the sole-current-user case —
  // that one is already the visible "Mine" chip and would otherwise show twice.
  filters.owner.forEach((id) => {
    if (mineActive && id === user?.id) return;
    activeChips.push({ key: `owner-${id}`, label: `Assigned: ${ownerLabel(id)}`, onRemove: () => apply({ owner: filters.owner.filter((o) => o !== id) }) });
  });
  if (filters.unassigned) {
    activeChips.push({ key: 'unassigned', label: 'Unassigned', onRemove: () => apply({ unassigned: false }) });
  }
  filters.eligibility.forEach((v) => {
    const el = INCOMING_ELIGIBILITY_OPTIONS.find((o) => o.value === v);
    activeChips.push({ key: `eligibility-${v}`, label: `Eligibility: ${el?.label || v}`, onRemove: () => apply({ eligibility: filters.eligibility.filter((x) => x !== v) }) });
  });
  filters.milestone.forEach((v) =>
    activeChips.push({
      key: `milestone-${v}`,
      label: `Milestone: ${MILESTONE_LABELS[v as keyof typeof MILESTONE_LABELS] || v}`,
      onRemove: () => apply({ milestone: filters.milestone.filter((x) => x !== v) }),
    }),
  );

  return (
    <div className="space-y-4">
      <PageHeader
        icon={HeaderIcon}
        title={copy.title}
        count={count ?? null}
        subtitle={subtitle}
        actions={<RefreshButton onClick={refresh} loading={loading} label="Refresh conversations" />}
      />

      {/* Toolbar: search + quick chips + Add-filter popover + active-filter chips.
          On phones the row is one swipeable strip (mobileScroll) so the queue
          starts under a short strip instead of a wall of chips — and the search
          box sits ABOVE the strip at full width (one mount, placed per tier),
          so the view toggles are the strip's first visible items. */}
      {isPhone && (
        <SearchInput
          value={filters.q}
          onChange={(v) => apply({ q: v }, true)}
          placeholder="Search name, phone, or email…"
          label="Search conversations"
          className="w-full"
        />
      )}
      <FilterBar mobileScroll active={<ActiveFilters chips={activeChips} onClearAll={hasFilters ? clearFilters : undefined} />}>
        {!isPhone && (
          <SearchInput
            value={filters.q}
            onChange={(v) => apply({ q: v }, true)}
            placeholder="Search name, phone, or email…"
            label="Search conversations"
            className="flex-1 basis-56 max-sm:shrink-0"
          />
        )}
        {!!user?.id && (
          <FilterChip active={mineActive} onClick={toggleMine}>Mine</FilterChip>
        )}
        {mode === 'active' && !filters.showArchived && (
          <FilterChip
            active={filters.needsReplyOnly}
            onClick={() => {
              const nextOn = !filters.needsReplyOnly;
              setPref(NEEDS_REPLY_PREF, String(nextOn));
              apply({ needsReplyOnly: nextOn });
            }}
            icon={<MessagesSquare className="h-4 w-4" />}
          >
            Awaiting my reply
          </FilterChip>
        )}
        {mode === 'active' && !filters.showArchived && (
          <FilterChip
            active={filters.agentOnly}
            onClick={() => apply({ agentOnly: !filters.agentOnly })}
            icon={<Bot className="h-4 w-4" />}
          >
            Agent-owned
          </FilterChip>
        )}
        {mode === 'active' && (
          <FilterChip
            active={filters.showArchived}
            onClick={() => apply({ showArchived: !filters.showArchived })}
            icon={<Archive className="h-4 w-4" />}
          >
            Show archived
          </FilterChip>
        )}
        {view !== 'archived' && (
          <FilterChip
            active={filters.showSnoozed}
            onClick={() => apply({ showSnoozed: !filters.showSnoozed })}
            icon={<MoonStar className="h-4 w-4" />}
          >
            Show snoozed
          </FilterChip>
        )}
        <FilterSelect value={filters.sort} onChange={(v) => apply({ sort: v })} auto label="Sort order">
          <option value="activity">Recent activity</option>
          <option value="eligibility">By eligibility</option>
          <option value="">Newest first</option>
          <option value="oldest">Oldest first</option>
        </FilterSelect>
        <AddFilterMenu filters={moreFilters} activeCount={moreFiltersCount} />
      </FilterBar>

      {error && (
        <Alert tone="error" icon={false}>{error}</Alert>
      )}

      {/* Results */}
      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="h-5 w-5 animate-spin text-primary-600" /></div>
      ) : rows.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <HeaderIcon className="mb-4 h-12 w-12 text-gray-300 dark:text-gray-600" />
          <h3 className="mb-1 text-lg font-medium text-gray-900 dark:text-gray-100">{copy.emptyTitle}</h3>
          <p className="max-w-sm text-sm text-gray-500 dark:text-gray-400">
            {hasFilters ? 'Try clearing your search, or the assignment and study filters.' : copy.emptyHint}
          </p>
        </div>
      ) : (
        <>
          <div ref={slice.containerRef} className="space-y-2">
            {/* Windowed above 100 cards (D-9): below that the slice is the whole
                list and no spacer renders, so the DOM is exactly what it was.
                The pads are in row PITCH (card + the stack's 8px gap) minus one
                gap, because the card next to each spacer keeps its own margin. */}
            {slice.topPad > 0 && (
              <div aria-hidden="true" data-spacer="1" style={{ height: slice.topPad }} />
            )}
            {rows.slice(slice.start, slice.end).map((c) => (
              <ConversationRow
                key={c.id}
                c={c}
                isArchivedView={view === 'archived'}
                rowNav={rowNav}
                onOpen={openContact}
                onOwnerChanged={onOwnerChanged}
                onToggleArchived={toggleRowArchived}
                onSnooze={snoozeRow}
              />
            ))}
            {slice.bottomPad > 0 && (
              <div aria-hidden="true" data-spacer="1" style={{ height: slice.bottomPad }} />
            )}
          </div>

          {hasMore && (
            <div className="flex justify-center">
              <button
                onClick={loadMore}
                disabled={loadingMore}
                className="inline-flex min-h-[44px] items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 sm:min-h-0"
              >
                {loadingMore ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {loadingMore ? 'Loading…' : 'Load more'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
